import { atom } from 'nanostores';
import { Play, Syncable } from '../types';
import { client } from '../apiClient';
import { $playFolders } from './playFolders';
import { $isOnline } from './app';

export const $plays = atom<Syncable<Array<Play>>>([])
export const $playsIsLoading = atom<boolean>(true)

async function fetchPlaysFromCache() {
  let allPlays: Syncable<Play>[] = [];
  try {
    const playCache = await caches.open('plays')
    if (playCache) {
      const response = await playCache.match('plays')
      if (response) {
        const data = await response.json()
        allPlays = data as Syncable<Play>[]
      } else {
        console.log('noresponse', 'plays')
      }
    }
  } catch (e) {
    console.error('fetchPlaysFromCache::error', e)
  }
  return allPlays
}

async function fetchPlays(): Promise<Array<Syncable<Play>>> {
  let allPlays: Syncable<Play>[] = [];
  let nextToken: string | undefined | null = undefined;
  let hasError: any[] | undefined = undefined;

  if (!$isOnline.get()) {
    return await fetchPlaysFromCache()
  }

  do {
    const { data: plays, errors, nextToken: nT } = await client.models.Play.list({ nextToken, limit: 150 })
    if (!plays && errors && errors.length > 0) {
      hasError = errors
    }
    if (plays && plays.length > 0) {
      allPlays = allPlays.concat(plays as Play[])
    }
    nextToken = nT

  } while (nextToken && !hasError)

  if (hasError) {
    return await fetchPlaysFromCache()
  }

  const playsCache = caches.open('plays')
  if (playsCache) {
    playsCache.then(async cache => {
      const blob = new Blob([JSON.stringify(allPlays)], { type: 'application/json' })
      const response = new Response(blob)
      await cache.put('plays', response)
      console.log('plays saved to cache')
    }).catch(() => {
      console.error("Failed to save plays to cache")
    })
  }

  return allPlays
}

async function addPlayToSyncQueue(play: Syncable<Play>) {
  console.log('addPlayToSyncQueue', play)
  const currentDraftCache = caches.open('draftPlays')
  if (currentDraftCache) {
    currentDraftCache.then(cache => {
      const blob = new Blob([JSON.stringify(play)], { type: 'application/json' })
      const response = new Response(blob)
      cache.put(`play-${play.id}`, response)
    }).catch(() => {
      throw new Error("Failed to save play to sync queue", { cause: 'Failed to open cache'})
    })
  }
}

const removePlayFromSyncQueue = async (play: Syncable<Play>) => {
  const currentDraftCache = caches.open('draftPlays')
  currentDraftCache.then(cache => {
    cache.delete(`play-${play.id}`)
  }).catch(() => {
    throw new Error("Failed to remove play from sync queue", { cause: 'Failed to open cache'})
  })
}

// A snapshot carrying a paths KEY (even with a null value) is rejected by a
// backend whose schema predates the Snapshot.paths field — GraphQL validates
// input field names regardless of value. Retrying with the key stripped
// keeps saving functional until the backend is deployed; the paths stay in
// the local store and offline cache.
const playHasPaths = (play: Play): boolean =>
  (play.snapshots || []).some((snapshot) => snapshot && 'paths' in snapshot)

const stripPathsFromPlay = <T extends Play>(play: T): T => ({
  ...play,
  snapshots: (play.snapshots || []).map((snapshot) => {
    if (!snapshot) {
      return snapshot
    }
    const { paths, ...rest } = snapshot
    return rest
  }),
})

export async function savePlay(play: Syncable<Play>, fromSync?: boolean): Promise<Syncable<Play>> {
  if (!$isOnline.get()) {
    if (fromSync) {
      return play
    }
    await addPlayToSyncQueue(play)
    const _plays = $plays.get()
    const index = _plays.findIndex(p => p.id === play.id)
    if (index !== -1) {
      const updatedPlays = [..._plays]
      updatedPlays[index] = play
      $plays.set(updatedPlays)
    } else {
      $plays.set([..._plays, play])
    }
    return play
  }
  const { isDraft, synced, ...payload } = play

  if (play.isDraft) {
    let { data, errors } = await client.models.Play.create(payload)
    if (errors && errors.length > 0 && playHasPaths(payload)) {
      console.warn('savePlay::create failed, retrying without recorded paths (backend schema may predate Snapshot.paths)', errors);
      ({ data, errors } = await client.models.Play.create(stripPathsFromPlay(payload)))
    }
    if (data && data.id === play.id) {
      play.synced = true
      play.isDraft = false
      void removePlayFromSyncQueue(play)
    }

    if (errors && errors.length > 0 && !fromSync) {
      await addPlayToSyncQueue(play)
    }
    const _plays = $plays.get()
    const index = _plays.findIndex(p => p.id === play.id)
    if (index !== -1) {
      const updatedPlays = [..._plays]
      updatedPlays[index] = play
      $plays.set(updatedPlays)
    } else {
      $plays.set([..._plays, play])
    }
  } else {
    let { data, errors } = await client.models.Play.update({
      ...payload,
      id: play.id!
    })
    if (errors && errors.length > 0 && playHasPaths(payload)) {
      console.warn('savePlay::update failed, retrying without recorded paths (backend schema may predate Snapshot.paths)', errors);
      ({ data, errors } = await client.models.Play.update({
        ...stripPathsFromPlay(payload),
        id: play.id!
      }))
    }
    if (data && data.id === play.id) {
      play.synced = true
      void removePlayFromSyncQueue(play)
    }
    if (errors && errors.length > 0 && !fromSync) {
      await addPlayToSyncQueue(play)
    }
    const _plays = $plays.get()
    const index = _plays.findIndex(p => p.id === play.id)
    if (index !== -1) {
      const updatedPlays = [..._plays]
      updatedPlays[index] = play
      $plays.set(updatedPlays)
    }
  }

  return play
}

async function getUnsyncedPlays() {
  let plays: Syncable<Play>[] = []
  const currentDraftCache = await caches.open('draftPlays')
  try {
    const keys = await currentDraftCache.keys()
    const promises = keys.map(async key => {
      const response = await currentDraftCache.match(key);
        if (response) {
            return response.json();
        }
    })
    const data = await Promise.all(promises)
    plays = data.filter(p => p !== null) as Syncable<Play>[]
  } catch (e) {
    console.error("Failed to get keys from cache", { cause: e })
  }
  console.log('CACCCHHHEEE:::getUnsyncedPlays', plays)
  return plays
}

export async function getPlays() {
  $playsIsLoading.set(true)
  const plays = await fetchPlays()
  console.log('getUnsyncedPlays', 'plays:')
  const unsyncedPlays = await getUnsyncedPlays()
  const filteredPlays = plays.filter(p => !unsyncedPlays.some(up => up.id === p.id))
  $plays.set([...filteredPlays, ...unsyncedPlays])
  $playsIsLoading.set(false)
  return [...plays, ...unsyncedPlays]
}

export async function syncPlays() {
  try {
    const draftCache = await getUnsyncedPlays()
    if (draftCache.length > 0) {
      console.log('syncPlays', 'draftCache:', draftCache)
      await Promise.all(draftCache.map(async play => {
        return savePlay(play, true)
      }));
    }
  } catch (e) {}
}